import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight, CalendarDays, Clock3 } from "lucide-react";
import API from "../services/api";
import BottomNav from "../components/BottomNav";

function Wallet() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [wallet, setWallet] = useState({
    deposit: 0,
    winnings: 0,
    balance: 0,
  });
  const [transactions, setTransactions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => getTodayDate());

  const formatMoney = (value) => `Rs ${Number(value || 0).toFixed(2)}`;

  const fetchWallet = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await API.get(`/wallet/${user.id}`);
      setWallet(res.data || { deposit: 0, winnings: 0, balance: 0 });
    } catch (err) {
      console.log(err);
      setWallet({ deposit: 0, winnings: 0, balance: 0 });
    }
  }, [user]);

  const fetchTransactions = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await API.get(`/transactions/${user.id}`);
      setTransactions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.log(err);
      setTransactions([]);
    }
  }, [user]);

  useEffect(() => {
    if (!user?.id) {
      navigate("/login");
      return;
    }

    fetchWallet();
    fetchTransactions();
  }, [fetchTransactions, fetchWallet, navigate, user]);

  const walletTransactions = transactions.filter((item) => {
    const type = String(item.type || "").toLowerCase();
    return type === "deposit" || type === "withdraw";
  });

  const pendingTransactions = walletTransactions.filter((item) => String(item.status || "").toLowerCase() === "pending");
  const completedTransactions = walletTransactions.filter((item) => String(item.status || "").toLowerCase() !== "pending");
  const filteredPendingTransactions = pendingTransactions.filter((item) => !selectedDate || toDateKey(item.created_at) === selectedDate);
  const filteredCompletedTransactions = completedTransactions.filter((item) => !selectedDate || toDateKey(item.created_at) === selectedDate);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-blue-50 to-white pb-24">
      <div className="w-full lg:max-w-[1280px] lg:mx-auto lg:px-6">
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-950 via-blue-700 to-cyan-500 p-4 lg:p-6 text-white rounded-[24px] lg:rounded-[30px] mx-3 lg:mx-0 mt-3 lg:mt-6 shadow-[0_18px_38px_rgba(37,99,235,0.28)] border border-blue-200/50">
        <div className="pointer-events-none absolute -right-12 -top-14 h-36 w-36 rounded-full bg-white/20 blur-xl" />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(115deg,transparent_0%,rgba(255,255,255,.16)_42%,transparent_62%)]" />
        <p className="relative text-[10px] opacity-90 tracking-[0.18em] font-extrabold">WALLET BALANCE</p>
        <h1 className="relative text-[34px] lg:text-5xl font-black mt-1 tracking-tight drop-shadow-[0_6px_14px_rgba(0,0,0,.24)]">{formatMoney(wallet.balance)}</h1>

        <div className="relative grid grid-cols-2 gap-2 mt-3 text-xs bg-white/14 backdrop-blur-sm p-2.5 rounded-xl border border-white/20">
          <div>
            <p className="opacity-70 uppercase text-[10px] font-bold tracking-[0.08em]">Deposit</p>
            <p className="font-black text-base">{formatMoney(wallet.deposit)}</p>
          </div>

          <div>
            <p className="opacity-70 uppercase text-[10px] font-bold tracking-[0.08em]">Winnings</p>
            <p className="font-black text-base text-cyan-100">{formatMoney(wallet.winnings)}</p>
          </div>
        </div>

        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={() => navigate("/deposit")}
            className="flex-1 bg-white text-blue-700 py-2.5 rounded-xl text-sm font-extrabold shadow-sm"
          >
            Add Money
          </button>

          <button
            type="button"
            onClick={() => navigate("/withdraw")}
            className="flex-1 bg-white/12 border border-white/40 py-2.5 rounded-xl text-sm font-extrabold"
          >
            Withdraw
          </button>
        </div>
      </div>

      <div className="px-3 lg:px-0 mt-3 lg:mt-5">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div>
            <p className="text-[15px] text-slate-950 font-black tracking-tight m-0">Wallet Transactions</p>
            <p className="text-[11px] text-slate-400 font-semibold m-0 mt-0.5">Deposit, withdraw, debit and credit history</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 bg-white border border-blue-100 rounded-full px-2.5 h-9 text-xs font-bold text-blue-900 shadow-sm">
              <CalendarDays size={14} />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-transparent outline-none text-xs font-bold text-slate-700"
              />
            </div>
            {selectedDate ? (
              <button
                type="button"
                onClick={() => setSelectedDate("")}
                className="h-9 px-3 rounded-full border border-blue-100 bg-white text-slate-600 text-xs font-bold"
              >
                All Dates
              </button>
            ) : null}
            {!selectedDate ? (
              <button
                type="button"
                onClick={() => setSelectedDate(getTodayDate())}
                className="h-9 px-3 rounded-full border border-blue-200 bg-blue-50 text-blue-700 text-xs font-bold"
              >
                Today
              </button>
            ) : null}
          </div>
        </div>

        <div className="space-y-4 lg:grid lg:grid-cols-2 lg:gap-5 lg:space-y-0">
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] text-amber-700 font-black tracking-[0.14em] m-0">PENDING</p>
              <span className="text-[11px] font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
                {filteredPendingTransactions.length} entries
              </span>
            </div>

            {filteredPendingTransactions.length === 0 ? (
              <div className="bg-white border border-dashed border-gray-200 rounded-xl px-4 py-4 text-xs font-semibold text-gray-400 text-center">
                No pending deposit or withdraw entry
              </div>
            ) : (
              <div className="grid gap-2">
                {filteredPendingTransactions.map((item) => (
                  <TransactionCard key={`pending-${item.type}-${item.id}-${item.created_at}`} item={item} formatMoney={formatMoney} />
                ))}
              </div>
            )}
          </section>

          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] text-blue-900 font-black tracking-[0.14em] m-0">HISTORY</p>
              <span className="text-[11px] font-bold text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-2.5 py-1">
                {filteredCompletedTransactions.length} entries
              </span>
            </div>

            {filteredCompletedTransactions.length === 0 ? (
              <div className="bg-white border border-dashed border-gray-200 rounded-xl px-4 py-4 text-xs font-semibold text-gray-400 text-center">
                No deposit or withdraw history found
              </div>
            ) : (
              <div className="grid gap-2">
                {filteredCompletedTransactions.map((item) => (
                  <TransactionCard key={`history-${item.type}-${item.id}-${item.created_at}`} item={item} formatMoney={formatMoney} />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
      </div>

      <BottomNav />
    </div>
  );
}

function TransactionCard({ item, formatMoney }) {
  const type = String(item.type || "").toLowerCase();
  const isDebit = type === "withdraw";
  const status = String(item.status || "").toLowerCase();
  const isPending = status === "pending";
  const bonusAmount = Number(item.bonus_amount || 0);
  const hasWalletStamp = item.wallet_balance !== null && item.wallet_balance !== undefined && item.wallet_balance !== "";

  return (
    <div className="bg-white p-3 rounded-[18px] border border-blue-50 shadow-[0_8px_22px_rgba(15,23,42,0.05)] flex items-start justify-between gap-3">
      <div className="flex items-start gap-2.5 min-w-0">
        <div className={`w-10 h-10 rounded-[14px] grid place-items-center flex-shrink-0 ${isDebit ? "bg-red-50 text-red-500" : "bg-green-50 text-green-600"}`}>
          {isDebit ? <ArrowUpRight size={18} /> : <ArrowDownLeft size={18} />}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-black text-[14px] tracking-tight text-slate-900 m-0">
              {isDebit ? "Withdraw" : "Deposit"}
            </p>
            <span className={`text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wide ${isPending ? "bg-amber-50 text-amber-700 border border-amber-200" : isDebit ? "bg-red-50 text-red-600 border border-red-100" : "bg-green-50 text-green-700 border border-green-100"}`}>
              {isPending ? "Pending" : isDebit ? "Debit" : "Credit"}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-slate-400">
            <Clock3 size={13} />
            <span>{formatDateTime(item.created_at)}</span>
          </div>
          {!!item.utr && (
            <div className="mt-1 text-[11px] text-blue-700 font-bold">
              UTR: {item.utr}
            </div>
          )}
        </div>
      </div>

      <div className="text-right flex-shrink-0">
        {bonusAmount > 0 && (
          <p className="inline-flex m-0 mb-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[10px] font-black text-amber-700">
            Bonus +{formatMoney(bonusAmount)}
          </p>
        )}
        <p className={`font-black text-[14px] tracking-tight m-0 ${isDebit ? "text-red-500" : "text-green-600"}`}>
          {isDebit ? "-" : "+"}{formatMoney(Math.abs(Number(item.amount || 0)))}
        </p>
        {hasWalletStamp && (
          <p className="inline-flex m-0 mt-1 px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-[10px] font-extrabold text-slate-500">
            Bal {formatMoney(item.wallet_balance)}
          </p>
        )}
        <p className="text-[10px] text-gray-400 capitalize mt-1 m-0 font-bold">{status || "-"}</p>
      </div>
    </div>
  );
}

function toDateKey(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toISOString().slice(0, 10);
}

function getTodayDate() {
  return new Date().toISOString().slice(0, 10);
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default Wallet;
